/* Campaign dates come from Flow. Prices and product eligibility come from Shopify. */
(() => {
  'use strict';
  const MAX_RULE_AGE = 22 * 60 * 1000;
  function activeRules(data, now) {
    const synced = Date.parse(data?.syncedAt);
    if (!Number.isFinite(synced) || now - synced > MAX_RULE_AGE || synced - now > 60000) return [];
    if (!Array.isArray(data.discounts) || data.discounts.length >= 100) return [];
    return data.discounts.filter(r => r.kind === 'DiscountAutomaticBasic' &&
      r.allCustomers === true && r.oneTime === true && r.minimum === false &&
      Number(r.percentage) > 0 && Number(r.percentage) < 1 &&
      r.title && Date.parse(r.startsAt) <= now &&
      (!r.endsAt || Date.parse(r.endsAt) > now));
  }
  function verifyQuote(rules, line, variant, currency) {
    if (!variant?.available || !line || line.quantity !== 1 ||
      line.merchandise?.id !== 'gid://shopify/ProductVariant/' + variant.id) return null;
    const total = line.cost?.totalAmount, base = line.merchandise?.price;
    if (total?.currencyCode !== currency || base?.currencyCode !== currency) return null;
    // Liquid's price units are hundredths even for zero-decimal currencies.
    const before = Math.round(Number(base.amount) * 100);
    const after = Math.round(Number(total.amount) * 100);
    if (before !== variant.price || !Number.isSafeInteger(after) || after < 0 || after >= before) return null;
    const allocations = line.discountAllocations || [];
    if (allocations.length !== 1 || allocations[0].__typename !== 'CartAutomaticDiscountAllocation') return null;
    const matches = rules.filter(r => r.title === allocations[0].title);
    if (matches.length !== 1) return null;
    const rule = matches[0], saving = before - after;
    const allocated = Math.round(Number(allocations[0].discountedAmount?.amount) * 100);
    if (allocated !== saving || allocations[0].discountedAmount?.currencyCode !== currency) return null;
    // One minor-unit tolerance accounts for Shopify rounding, while rejecting stale percentages.
    const minorUnit = 100 / 10 ** new Intl.NumberFormat('en', {style:'currency',currency}).resolvedOptions().maximumFractionDigits;
    if (Math.abs(saving - before * Number(rule.percentage)) > minorUnit) return null;
    return {rule, before, after, saving};
  }
  function timeParts(milliseconds) {
    const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
    return [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60];
  }
  function activityTitle(rule, names, locale, now) {
    const synced = Date.parse(names?.syncedAt);
    if (!Number.isFinite(synced) || now - synced > 3600000 || synced - now > 60000 ||
      !Array.isArray(names.discounts)) return rule.title;
    const matches = names.discounts.filter(entry => entry.id === rule.id && entry.title === rule.title);
    if (matches.length !== 1 || !Array.isArray(matches[0].translations)) return rule.title;
    const translations = matches[0].translations.filter(entry => entry.locale === String(locale).toLowerCase());
    return translations.length === 1 && typeof translations[0].title === 'string' && translations[0].title.trim()
      ? translations[0].title.trim() : rule.title;
  }
  // A promotion can be active without applying to this product or market.
  function verifyBaseQuote(line, variant, currency) {
    if (!variant?.available || line?.quantity !== 1 ||
      line.merchandise?.id !== 'gid://shopify/ProductVariant/' + variant.id ||
      line.merchandise?.price?.currencyCode !== currency || line.cost?.totalAmount?.currencyCode !== currency ||
      (line.discountAllocations || []).length) return false;
    return Math.round(Number(line.merchandise.price.amount) * 100) === variant.price &&
      Math.round(Number(line.cost.totalAmount.amount) * 100) === variant.price;
  }
  function ruleFingerprint(rules) {
    return JSON.stringify(rules.map(r => [r.id, r.title, r.startsAt, r.endsAt, Number(r.percentage)]).sort());
  }
  function quotedSchema(source, variant, currency, quote) {
    const node = source?.hasVariant?.find(v => {
      try {return new URL(v.url).searchParams.get('variant') === String(variant?.id);} catch (_) {return false;}
    });
    if (!node?.offers || node.offers.priceCurrency !== currency || !variant?.available ||
      Math.round(Number(node.offers.price) * 100) !== variant.price ||
      !Number.isSafeInteger(quote?.after) || quote.after < 0 || quote.before !== variant.price) return null;
    const offer = {...node.offers, price:quote.after / 100};
    if (quote.rule) {
      offer.priceSpecification = {
        '@type':'UnitPriceSpecification', priceType:'https://schema.org/StrikethroughPrice',
        price:quote.before / 100, priceCurrency:currency
      };
      if (quote.rule.endsAt) {
        offer.validThrough = quote.rule.endsAt;
        offer.priceValidUntil = quote.rule.endsAt.slice(0, 10);
      }
    }
    // Keep only the quoted configuration: an unquoted sibling may have different eligibility.
    // Cloning the offer retains shipping, handling, returns, condition and availability.
    return {...source, hasVariant:[{...node, offers:offer}]};
  }
  const api = {activeRules, verifyQuote, timeParts, activityTitle, verifyBaseQuote, ruleFingerprint, quotedSchema};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined') return;
  const LINE = `id quantity merchandise { ... on ProductVariant { id price { amount currencyCode } } }
    cost { totalAmount { amount currencyCode } }
    discountAllocations { __typename discountedAmount { amount currencyCode } ... on CartAutomaticDiscountAllocation { title } }`;
  function init(section, {money, translate}) {
    const configElement = section.querySelector('[data-r2-promotion-config]');
    const panel = section.querySelector('[data-r2-promotion]');
    if (!configElement || !panel) return {update() {}};
    if (section.r2Promotion) return section.r2Promotion;
    const config = JSON.parse(configElement.textContent), strings = config.strings;
    const schemaElement = section.querySelector('[data-r2-product-schema]');
    const schemaInitiallyPending = schemaElement?.type === 'application/json';
    let schemaSource;
    try {schemaSource = JSON.parse(schemaElement?.textContent || 'null');} catch (_) { /* Schema must never block the purchase form. */ }
    let data = config.rules, activityNames = config.activityNames, variant, verified, pendingRule, cartId, lineId, revision = 0;
    let quoteQueue = Promise.resolve(), debounce, inFlight = false, dead = false;
    let clockAt = Date.now(), clockTick = performance.now(), serverKnown = false;
    let lastQuoteAt = 0;
    let baseQuote = null;
    const now = () => clockAt + performance.now() - clockTick;
    const currency = config.currency;
    const price = section.querySelector('[data-r2-price]');
    const compare = section.querySelector('[data-r2-compare-price]');
    const badge = section.querySelector('[data-r2-saving]');
    const sticky = section.querySelector('[data-r2-sticky-price]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const format = (value, values) => value.replace(/__(\w+)__/g, (_, key) => values[key] ?? '');
    function syncSchema() {
      if (!schemaElement || !schemaSource) return;
      const rules = activeRules(data, now());
      let graph = null, state = 'pending';
      if (!rules.length && (serverKnown || !schemaInitiallyPending)) {
        graph = schemaSource;
        state = 'base';
      } else if (serverKnown) {
        const quote = verified || (baseQuote?.variantId === variant?.id &&
          baseQuote.fingerprint === ruleFingerprint(rules) && now() - lastQuoteAt <= 75000
          ? {before:variant.price, after:variant.price} : null);
        if (quote) graph = quotedSchema(schemaSource, variant, currency, quote);
        if (graph) state = verified ? 'promotion' : 'verified-base';
      }
      // Disable unverified prices before replacing data; never add a competing Product graph.
      if (!graph) schemaElement.type = 'application/json';
      const json = JSON.stringify(graph || schemaSource).replace(/</g, '\\u003c');
      if (schemaElement.textContent !== json) schemaElement.textContent = json;
      if (graph) schemaElement.type = 'application/ld+json';
      schemaElement.dataset.r2SchemaPriceState = state;
    }
    function syncClock(response, started) {
      const server = Date.parse(response.headers.get('Date'));
      if (Number.isFinite(server)) {
        clockAt = server + (performance.now() - started) / 2;
        clockTick = performance.now();
        serverKnown = true;
      }
    }
    function setPending(pending) {
      section.toggleAttribute('data-promotion-pending', pending);
      panel.setAttribute('aria-busy', String(pending));
      // Conceal only unverified amounts; keep the panel and timer in the layout.
      [price, compare, badge, sticky, panel.querySelector('[data-r2-promotion-saving]')].forEach(element => {
        if (!element) return;
        if (pending) element.setAttribute('aria-hidden', 'true');
        else element.removeAttribute('aria-hidden');
      });
    }
    function basePrice() {
      pendingRule = null;
      setPending(false);
      panel.hidden = true;
      section.removeAttribute('data-promotion-active');
      if (!variant) return; // The variant controller owns unavailable-combination copy.
      const display = money(variant.price) + ' ' + currency;
      if (price.textContent !== display) price.textContent = display;
      if (sticky && sticky.textContent !== display) sticky.textContent = display;
      const sale = variant.compare_at_price > variant.price;
      compare.hidden = badge.hidden = !sale;
      const comparison = sale ? money(variant.compare_at_price) : '';
      const saving = sale ? translate('Save') + ' ' + money(variant.compare_at_price - variant.price) : '';
      if (compare.textContent !== comparison) compare.textContent = comparison;
      if (badge.textContent !== saving) badge.textContent = saving;
    }
    function render() {
      const currentRule = verified?.rule || pendingRule;
      if (!currentRule || !variant?.available || !serverKnown || !activeRules(data, now()).some(r => r.id === currentRule.id &&
        r.title === currentRule.title && r.endsAt === currentRule.endsAt && r.percentage === currentRule.percentage)) {
        verified = null;
        basePrice();
        syncSchema();
        return;
      }
      const rule = currentRule;
      setPending(!verified);
      if (verified) {
        const {before, after, saving} = verified;
        const display = money(after) + ' ' + currency;
        if (price.textContent !== display) price.textContent = display;
        if (sticky && sticky.textContent !== display) sticky.textContent = display;
        compare.hidden = badge.hidden = false;
        compare.textContent = money(before);
        badge.textContent = format(strings.off, {percent:Math.round(Number(rule.percentage) * 10000) / 100});
        panel.querySelector('[data-r2-promotion-saving]').textContent = format(strings.save, {amount:money(saving)});
      }
      panel.querySelector('[data-r2-promotion-title]').textContent = activityTitle(rule, activityNames, config.locale, now());
      const clock = panel.querySelector('[data-r2-promotion-clock]');
      clock.hidden = !rule.endsAt;
      panel.querySelector('[data-r2-promotion-limited]').hidden = !rule.endsAt;
      if (rule.endsAt) {
        const parts = timeParts(Date.parse(rule.endsAt) - now());
        const timer = panel.querySelector('[data-r2-promotion-timer]');
        panel.querySelector('[data-r2-promotion-day]').hidden = !parts[0];
        timer.querySelectorAll('[data-r2-promotion-number]').forEach((digit, i) => {
          const value = String(parts[i]).padStart(2, '0');
          if (digit.textContent === value) return;
          digit.textContent = value;
          // Animate only a changing, visible number. Never flash the whole offer.
          if (!panel.hidden && !digit.closest('[hidden]') && !reduceMotion.matches && digit.animate) {
            digit.animate([
              {opacity:0.35,transform:'translateY(-5px)'},
              {opacity:1,transform:'translateY(0)'}
            ], {duration:220,easing:'ease-out'});
          }
        });
        timer.setAttribute('aria-label', parts.map((v,i) => v + ' ' + [strings.days,strings.hours,strings.minutes,strings.seconds][i]).join(', '));
      }
      section.setAttribute('data-promotion-active', '');
      panel.hidden = false;
      syncSchema();
    }
    async function quote(selected, ticket) {
      if (dead || ticket !== revision || !selected?.available || !activeRules(data, now()).length) return;
      inFlight = true;
      const create = !cartId || !lineId;
      const query = create
        ? `mutation ReloopoPromotionQuote($input:CartInput!){cartCreate(input:$input){cart{id lines(first:1){nodes{${LINE}}}} userErrors{message}}}`
        : `mutation ReloopoPromotionQuote($cartId:ID!,$lines:[CartLineUpdateInput!]!){cartLinesUpdate(cartId:$cartId,lines:$lines){cart{id lines(first:1){nodes{${LINE}}}} userErrors{message}}}`;
      const merchandiseId = 'gid://shopify/ProductVariant/' + selected.id;
      const variables = create
        ? {input:{buyerIdentity:{countryCode:config.country},lines:[{merchandiseId,quantity:1}]}}
        : {cartId,lines:[{id:lineId,merchandiseId,quantity:1}]};
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const started = performance.now();
        const response = await fetch('/api/2026-07/graphql.json', {
          method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},
          credentials:'same-origin',signal:controller.signal,body:JSON.stringify({query,variables})
        });
        syncClock(response, started);
        if (!response.ok) throw Error('Quote unavailable');
        const body = await response.json(), result = body.data?.[create ? 'cartCreate' : 'cartLinesUpdate'];
        if (body.errors?.length || result?.userErrors?.length || !result?.cart) throw Error('Quote unavailable');
        const line = result.cart.lines.nodes[0];
        cartId = result.cart.id;
        lineId = line?.id;
        if (ticket !== revision || dead) return;
        pendingRule = null;
        verified = verifyQuote(activeRules(data, now()), line, selected, currency);
        baseQuote = !verified && verifyBaseQuote(line, selected, currency)
          ? {variantId:selected.id, fingerprint:ruleFingerprint(activeRules(data, now()))} : null;
        lastQuoteAt = now();
      } catch (_) {
        cartId = lineId = null;
        if (ticket === revision) {verified = null;pendingRule = null;baseQuote = null;}
      } finally {
        clearTimeout(timeout);
        inFlight = false;
        if (ticket === revision && !dead) render();
      }
    }
    function scheduleQuote() {
      clearTimeout(debounce);
      const selected = variant, ticket = revision;
      debounce = setTimeout(() => {
        quoteQueue = quoteQueue.catch(() => {}).then(() => quote(selected, ticket));
      }, 150);
    }
    async function refresh() {
      if (dead || document.hidden) return;
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', 'r2-promotion-data');
        url.searchParams.set('_promo', String(Date.now()));
        const started = performance.now();
        const response = await fetch(url, {cache:'no-store',signal:controller.signal});
        syncClock(response, started);
        if (!response.ok) throw Error('Rules unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = JSON.parse(doc.querySelector('[data-r2-promotion-data]')?.textContent || 'null');
        data = fresh;
        activityNames = JSON.parse(doc.querySelector('[data-r2-activity-names]')?.textContent || 'null');
        if (!fresh) verified = null;
      } catch (_) {
        // Never prolong a cached offer beyond its own expiry or freshness limit.
      } finally {
        clearTimeout(timeout);
        render();
        scheduleQuote();
      }
    }
    const controller = {
      update(next) {
        if (next?.id !== variant?.id || next?.price !== variant?.price || next?.available !== variant?.available) {
          revision++;
          pendingRule = next?.available ? (verified?.rule || pendingRule) : null;
          verified = null;
          baseQuote = null;
          variant = next;
          render();
          scheduleQuote();
        } else {
          variant = next;
          render(); // Add-to-cart loading states must not reset the verified offer.
        }
      },
      destroy() {dead=true;clearTimeout(debounce);clearInterval(tick);clearInterval(poll);document.removeEventListener('visibilitychange', visible);window.removeEventListener('pageshow', refresh);}
    };
    const visible = () => {if (!document.hidden) refresh();};
    const tick = setInterval(() => {
      if (!section.isConnected) {controller.destroy();return;}
      if (verified && now() - lastQuoteAt > 75000) verified = null;
      if (!document.hidden) render();
    }, 1000);
    const poll = setInterval(() => {if (!inFlight) refresh();}, 60000);
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('pageshow', refresh);
    section.r2Promotion = controller;
    // Fetch once to anchor countdown to Shopify time, not the customer's device clock.
    refresh();
    return controller;
  }
  window.ReloopoPromotion = {...api, init};
})();
